/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MODELO GEO RESTRICTION ENGINE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Config-driven, OneTrust-aware geo restriction engine for Shopify.
 * All product/state rules are injected via window.MODELO_GEO_CONFIG
 * (set by snippets/modelo-geo-restriction.liquid via metaobject fields).
 *
 * Detection priority:
 *   1. OneTrust.getGeolocationData()   — primary (already loaded in theme)
 *   2. Fail-open if OneTrust times out  — do not block visitors on uncertainty
 *
 * Restriction modes:
 *   allowlist  → product is ONLY available in the listed states
 *   blocklist  → product is NOT available in the listed states
 *
 * Auto-init:
 *   window.MODELO_GEO_CONFIG must be set BEFORE this script loads.
 *   The snippet handles this by injecting the <script> block first.
 *
 * Shopify Customizer preview is always bypassed (fail-open).
 * Any detection error is also fail-open (do not block visitors on errors).
 *
 * Manual usage / testing:
 *   ModeloGeoRestriction.check('TX', window.MODELO_GEO_CONFIG)  → true/false
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

(function (global) {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
   * CONSTANTS & DEFAULTS
   * ───────────────────────────────────────────────────────────────────────── */

  var DEFAULTS = {
    enabled: true,
    restrictionType: 'allowlist',    // 'allowlist' | 'blocklist'
    states: [],                       // string[] — 2-letter US codes, uppercase
    redirectUrl: 'https://www.modelousa.com',
    redirectDelay: 100,              // milliseconds
    overlayId: '',
    oneTrustTimeout: 8000,            // ms to wait for OneTrust before failing open
    oneTrustPollInterval: 150,        // ms between polls
    productHandle: '',
    productTitle: '',
    debug: false,
    navHiddenCategories: []   // comma-separated category handles from nav_hidden_categories metaobject field
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * LOGGER
   * ───────────────────────────────────────────────────────────────────────── */

  function makeLogger(debug, handle) {
    var prefix = '[ModeloGeo' + (handle ? ':' + handle : '') + ']';
    return {
      log: function () {
        if (debug) {
          console.log.apply(console, [prefix].concat(Array.prototype.slice.call(arguments)));
        }
      },
      warn: function () {
        if (debug) {
          console.warn.apply(console, [prefix].concat(Array.prototype.slice.call(arguments)));
        }
      },
      error: function () {
        console.error.apply(console, [prefix].concat(Array.prototype.slice.call(arguments)));
      }
    };
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * HELPERS
   * ───────────────────────────────────────────────────────────────────────── */

  function parseStates(raw) {
    if (Array.isArray(raw)) {
      return raw.map(function (s) { return String(s).trim().toUpperCase(); }).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw.split(',').map(function (s) { return s.trim().toUpperCase(); }).filter(Boolean);
    }
    return [];
  }

  function parseHandles(raw) {
    if (Array.isArray(raw)) {
      return raw.map(function (s) { return String(s).trim().toLowerCase(); }).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw.split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
    }
    return [];
  }

  function mergeConfig(user) {
    var cfg = Object.assign({}, DEFAULTS, user);

    // Normalise field name variants coming from Liquid (snake_case vs camelCase)
    cfg.restrictionType = String(
      user.restrictionType || user.restriction_type || DEFAULTS.restrictionType
    ).toLowerCase().trim();

    cfg.states = parseStates(user.states || user.allowedStates || user.allowed_states || []);

    cfg.redirectUrl    = user.redirectUrl    || user.redirect_url    || DEFAULTS.redirectUrl;
    cfg.redirectDelay  = parseInt(user.redirectDelay  != null ? user.redirectDelay  : (user.redirect_delay  != null ? user.redirect_delay  : DEFAULTS.redirectDelay), 10);
    cfg.productHandle  = user.productHandle  || user.product_handle  || DEFAULTS.productHandle;
    cfg.productTitle   = user.productTitle   || user.product_title   || DEFAULTS.productTitle;
    cfg.navHiddenCategories = parseHandles(user.navHiddenCategories || user.nav_hidden_categories || []);
    cfg.oneTrustTimeout = parseInt(
      user.oneTrustTimeout != null ? user.oneTrustTimeout : (user.onetrust_timeout != null ? user.onetrust_timeout : DEFAULTS.oneTrustTimeout), 10
    );

    return cfg;
  }

  function isShopifyPreview() {
    return !!(
      global.Shopify &&
      (global.Shopify.designMode === true ||
        global.location.search.indexOf('preview_theme_id') !== -1)
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * DETECTION — OneTrust
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Polls until OneTrust.getGeolocationData() is available or times out.
   * Resolves with the stateCode string (e.g. 'TX') or null on timeout/error.
   *
   * @param {number}   timeoutMs
   * @param {number}   intervalMs
   * @param {Object}   log
   * @returns {Promise<string|null>}
   */
  function getStateFromOneTrust(timeoutMs, intervalMs, log) {
    return new Promise(function (resolve) {
      var elapsed = 0;

      function tryRead() {
        try {
          if (
            typeof global.OneTrust !== 'undefined' &&
            typeof global.OneTrust.getGeolocationData === 'function'
          ) {
            var geo = global.OneTrust.getGeolocationData();
            if (geo && geo.state) {
              log.log('OneTrust state detected:', geo.state, '| country:', geo.country);
              resolve(String(geo.state).toUpperCase());
              return true;
            }
          }
        } catch (e) {
          log.error('OneTrust read error:', e);
        }
        return false;
      }

      if (tryRead()) return;

      var timer = setInterval(function () {
        elapsed += intervalMs;
        if (tryRead()) {
          clearInterval(timer);
          return;
        }
        if (elapsed >= timeoutMs) {
          clearInterval(timer);
          log.warn('OneTrust timed out after ' + timeoutMs + 'ms — failing open');
          resolve(null);
        }
      }, intervalMs);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * ACCESS EVALUATION
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Returns true when the visitor should be BLOCKED.
   *
   * @param {string|null} stateCode       — resolved 2-letter code, e.g. 'TX'
   * @param {string[]}    states          — list from metaobject config
   * @param {string}      restrictionType — 'allowlist' | 'blocklist'
   * @returns {boolean}
   */
  function shouldBlock(stateCode, states, restrictionType) {
    // Unknown state — fail-open (never block on uncertainty)
    if (!stateCode) return false;

    var inList = states.indexOf(stateCode) !== -1;

    if (restrictionType === 'allowlist') {
      // Block if state is NOT in the allow-list
      return !inList;
    }
    if (restrictionType === 'blocklist') {
      // Block if state IS in the block-list
      return inList;
    }

    return false;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * DOM HELPERS
   * ───────────────────────────────────────────────────────────────────────── */

  function getEl(id) { return document.getElementById(id); }

  function showOverlay(overlayId) {
    var el = getEl(overlayId);
    if (el) el.style.display = 'flex';
  }

  function hideOverlay(overlayId) {
    var el = getEl(overlayId);
    if (el) el.style.display = 'none';
  }

  /**
   * Fires the redirect after the configured delay.
   */
  function triggerRedirect(url, delayMs, log) {
    log.log('Redirecting to', url, 'in', delayMs + 'ms');
    if (delayMs <= 0) {
      global.location.replace(url);
    } else {
      setTimeout(function () {
        global.location.replace(url);
      }, delayMs);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * NAV RESTRICTION
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Hides header nav items (desktop tab + content panel + mobile button)
   * for all category handles listed in cfg.navHiddenCategories.
   *
   * Elements must carry  data-geo-nav-key="{handle}"  in the HTML.
   * Pass 'all' as the only entry to hide every keyed nav item.
   *
   * @param {Object} cfg
   * @param {Object} log
   */
  function applyNavRestriction(cfg, log) {
    var handles = cfg.navHiddenCategories;
    if (!handles || handles.length === 0) return;

    log.log('Applying nav restriction for handles:', handles);

    var targets = handles[0] === 'all'
      ? Array.prototype.slice.call(document.querySelectorAll('[data-geo-nav-key]'))
      : handles.reduce(function (acc, handle) {
          var els = document.querySelectorAll('[data-geo-nav-key="' + handle + '"]');
          return acc.concat(Array.prototype.slice.call(els));
        }, []);

    targets.forEach(function (el) {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
      if (el.getAttribute('role') === 'tab') {
        el.setAttribute('tabindex', '-1');
      }
      log.log('Nav element hidden:', el.getAttribute('data-geo-nav-key'), el.tagName, el.id || '');
    });

    // If the currently active desktop tab was hidden, activate the first still-visible one
    var activeTab = document.querySelector('.products-submenu-item.active');
    if (activeTab && activeTab.style.display === 'none') {
      var firstVisible = Array.prototype.slice.call(
        document.querySelectorAll('.products-submenu-item')
      ).find(function (btn) { return btn.style.display !== 'none'; });
      if (firstVisible) {
        firstVisible.click();
        log.log('Active tab was hidden — switched to:', firstVisible.getAttribute('data-geo-nav-key'));
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────────── */

  var ModeloGeoRestriction = {

    /**
     * Main entry point. Reads config, detects visitor state, blocks or allows.
     *
     * @param {Object} userConfig — from window.MODELO_GEO_CONFIG (set by snippet)
     */
    init: function (userConfig) {
      var cfg = mergeConfig(userConfig || {});
      var log = makeLogger(cfg.debug, cfg.productHandle);

      log.log('=== ModeloGeoRestriction.init ===');
      log.log('Config:', cfg);

      // ── 1. Disabled? ──────────────────────────────────────────────────────
      if (!cfg.enabled) {
        log.log('Geo restriction is disabled — skipping.');
        hideOverlay(cfg.overlayId);
        return;
      }

      // ── 2. Shopify Customizer preview bypass ───────────────────────────────
      if (isShopifyPreview()) {
        log.warn('Shopify Customizer preview — geo restriction bypassed.');
        hideOverlay(cfg.overlayId);
        return;
      }

      // ── 3. Show loading overlay ────────────────────────────────────────────
      showOverlay(cfg.overlayId);

      // ── 4. Detect state via OneTrust ──────────────────────────────────────
      // Try OneTrust synchronously first (zero cost if already loaded)
      var immediateState = null;
      try {
        if (
          typeof global.OneTrust !== 'undefined' &&
          typeof global.OneTrust.getGeolocationData === 'function'
        ) {
          var geoSync = global.OneTrust.getGeolocationData();
          if (geoSync && geoSync.state) {
            immediateState = String(geoSync.state).toUpperCase();
            log.log('OneTrust state (sync):', immediateState);
          }
        }
      } catch (e) { /* ignore */ }

      if (immediateState) {
        // OneTrust was already loaded — no async needed
        var blocked = shouldBlock(immediateState, cfg.states, cfg.restrictionType);
        log.log('Decision (sync):', blocked ? 'BLOCKED' : 'ALLOWED');
        if (blocked) {
          applyNavRestriction(cfg, log);
          triggerRedirect(cfg.redirectUrl, cfg.redirectDelay, log);
        } else {
          hideOverlay(cfg.overlayId);
        }
        return;
      }

      // OneTrust not yet ready — wait for it; fail-open on timeout/null
      var otPromise = getStateFromOneTrust(cfg.oneTrustTimeout, cfg.oneTrustPollInterval, log);

      otPromise
        .then(function (resolvedState) {
          log.log('Resolved state:', resolvedState);
          var blocked = shouldBlock(resolvedState, cfg.states, cfg.restrictionType);
          log.log('Decision:', blocked ? 'BLOCKED' : 'ALLOWED');
          if (blocked) {
            applyNavRestriction(cfg, log);
            triggerRedirect(cfg.redirectUrl, cfg.redirectDelay, log);
          } else {
            hideOverlay(cfg.overlayId);
          }
        })
        .catch(function (err) {
          log.error('Detection pipeline error — fail-open:', err);
          hideOverlay(cfg.overlayId);
        });
    },

    /**
     * Nav-only entry point — runs on every page via the header section.
     * Performs OneTrust geo detection and ONLY hides nav items (no overlay, no redirect).
     *
     * @param {Object} userConfig — from window.MODELO_GEO_NAV_CONFIG (set by header)
     */
    initNavOnly: function (userConfig) {
      var cfg = mergeConfig(userConfig || {});
      var log = makeLogger(cfg.debug, 'nav');

      log.log('=== ModeloGeoRestriction.initNavOnly ===');
      log.log('Config:', cfg);

      if (!cfg.enabled) {
        log.log('Nav geo restriction disabled — skipping.');
        return;
      }

      if (isShopifyPreview()) {
        log.warn('Shopify Customizer preview — nav geo restriction bypassed.');
        return;
      }

      if (!cfg.navHiddenCategories || cfg.navHiddenCategories.length === 0) {
        log.log('No navHiddenCategories configured — skipping.');
        return;
      }

      // Try OneTrust synchronously first (zero cost if already loaded)
      var immediateState = null;
      try {
        if (
          typeof global.OneTrust !== 'undefined' &&
          typeof global.OneTrust.getGeolocationData === 'function'
        ) {
          var geoSync = global.OneTrust.getGeolocationData();
          if (geoSync && geoSync.state) {
            immediateState = String(geoSync.state).toUpperCase();
            log.log('OneTrust state (sync):', immediateState);
          }
        }
      } catch (e) { /* ignore */ }

      if (immediateState) {
        var blocked = shouldBlock(immediateState, cfg.states, cfg.restrictionType);
        log.log('Nav decision (sync):', blocked ? 'HIDE' : 'SHOW');
        if (blocked) applyNavRestriction(cfg, log);
        return;
      }

      // Async — wait for OneTrust; fail-open on timeout/null
      var otPromise = getStateFromOneTrust(cfg.oneTrustTimeout, cfg.oneTrustPollInterval, log);

      otPromise
        .then(function (resolvedState) {
          log.log('Nav resolved state:', resolvedState);
          var blocked = shouldBlock(resolvedState, cfg.states, cfg.restrictionType);
          log.log('Nav decision:', blocked ? 'HIDE' : 'SHOW');
          if (blocked) applyNavRestriction(cfg, log);
        })
        .catch(function (err) {
          log.error('Nav detection pipeline error — fail-open:', err);
        });
    },

    /**
     * Utility for testing/debugging: check whether a given state code
     * would be blocked under a given config — without touching the DOM.
     *
     * @param {string} stateCode   — e.g. 'TX'
     * @param {Object} userConfig  — optional, falls back to window.MODELO_GEO_CONFIG
     * @returns {boolean}
     */
    check: function (stateCode, userConfig) {
      var cfg = mergeConfig(userConfig || global.MODELO_GEO_CONFIG || {});
      return shouldBlock(String(stateCode).toUpperCase(), cfg.states, cfg.restrictionType);
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * REGISTER & AUTO-INIT
   * ───────────────────────────────────────────────────────────────────────── */

  global.ModeloGeoRestriction = ModeloGeoRestriction;

  // Auto-init page-level restriction (set by geo-restriction section/snippet)
  if (global.MODELO_GEO_CONFIG) {
    ModeloGeoRestriction.init(global.MODELO_GEO_CONFIG);
  }

  // Auto-init nav-only restriction (set by header section — runs on every page)
  if (global.MODELO_GEO_NAV_CONFIG) {
    ModeloGeoRestriction.initNavOnly(global.MODELO_GEO_NAV_CONFIG);
  }

})(window);
